/**
 * Module-level Regta JWT provider.
 *
 * Why module-level (not React state):
 *   - The api.ts `rpc()` helper, binary-endpoint fetches, SWR fetchers,
 *     setTimeout callbacks and event handlers all need a valid token,
 *     and most of them run outside the React render tree. A module-level
 *     getter is reachable from anywhere without prop-drilling, and it
 *     reads its mutable state (session + in-flight) directly so callers
 *     always see the latest value.
 *
 * Why in-flight dedupe:
 *   - When the tab comes back from background, multiple SWR queries
 *     refetch simultaneously. With a per-call refresh we'd issue N mint
 *     requests; the in-flight promise collapses them into one.
 *
 * Freshness check: returns the cached token if its `expiresAt` is more
 * than SAFETY_MS away. Otherwise mints. The `force` option bypasses the
 * cache (used by `rpc()`'s 401-retry path).
 *
 * The actual mint logic (Clerk getToken → mintRegtaJwt) lives in
 * `auth-context.tsx` and is registered via `registerTokenMinter` on
 * mount. The provider stays decoupled from React.
 */

export type TokenSession = { token: string; expiresAt: number };

const SAFETY_MS = 10_000;

let session: TokenSession | null = null;
let minter: ((opts?: { force?: boolean }) => Promise<TokenSession>) | null = null;
let inFlight: Promise<TokenSession> | null = null;
let onChange: ((s: TokenSession | null) => void) | null = null;

/**
 * Register the function that actually mints a Regta JWT. Called once
 * by AuthContext on mount. The `onChangeCb` is invoked whenever the
 * cached session changes so AuthContext can mirror it into React state
 * for UI gating (`isReady`, `token`).
 */
export function registerTokenMinter(
  fn: (opts?: { force?: boolean }) => Promise<TokenSession>,
  onChangeCb: (s: TokenSession | null) => void,
): void {
  minter = fn;
  onChange = onChangeCb;
}

/** Unregister and clear session (call on sign-out / unmount). */
export function clearTokenProvider(): void {
  session = null;
  minter = null;
  inFlight = null;
  onChange?.(null);
  onChange = null;
}

/** Clear only the session (e.g. on sign-out while keeping the minter wired). */
export function clearSession(): void {
  session = null;
  onChange?.(null);
}

export function peekSession(): TokenSession | null {
  return session;
}

/**
 * Resolve a valid Regta JWT.
 *
 *   - Returns the cached token if it is more than 10s from expiry.
 *   - Otherwise mints (or joins an in-flight mint).
 *   - `force: true` skips the cache but still aggregates with any
 *     in-flight mint.
 */
export async function getToken(opts?: { force?: boolean }): Promise<string> {
  if (
    !opts?.force &&
    session &&
    Date.now() < session.expiresAt * 1000 - SAFETY_MS
  ) {
    return session.token;
  }
  if (inFlight) {
    const next = await inFlight;
    return next.token;
  }
  if (!minter) {
    throw new Error('token_provider_not_ready');
  }
  inFlight = minter(opts).finally(() => {
    inFlight = null;
  });
  try {
    const next = await inFlight;
    session = next;
    onChange?.(next);
    return next.token;
  } catch (err) {
    // Surface failure to the caller; do not poison the cached session.
    throw err;
  }
}
